import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import {
  FaArrowRight,
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight,
  FaNewspaper,
  FaSearch,
  FaStar,
  FaTruck,
} from 'react-icons/fa';

const DEFAULT_BANNER = 'https://hub.mileageriot.com/assets/images/banner/banner.jpg';
const PAGE_SIZE = 12;

const getNewsImage = (image) => {
  if (!image || image.includes('logo_sm.png') || image.includes('logo.svg')) return DEFAULT_BANNER;
  return image.startsWith('http') ? image : DEFAULT_BANNER;
};

const cleanText = (value = '') => String(value)
  .replace(/<br\s*\/?>/gi, ' ')
  .replace(/<[^>]*>/g, '')
  .replace(/\s+/g, ' ')
  .trim();

const NewsCard = ({ item, featured, locale, t }) => {
  const articlePath = `/news/${item.slug || item.id}`;
  const isTruckersMP = item.source === 'truckersmp';
  const description = cleanText(item.description);
  const date = item.created_at
    ? new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(item.created_at))
    : '';

  const handleImageError = (event) => {
    event.currentTarget.onerror = null;
    event.currentTarget.src = DEFAULT_BANNER;
  };

  return (
    <article className={`news-card-upgraded${featured ? ' is-featured' : ''}`}>
      <Link to={articlePath} className="news-card-link" aria-label={`${item.title} — ${t('news.readMore')}`}>
        <div className="news-card-media">
          <img
            src={getNewsImage(item.image)}
            alt=""
            loading={featured ? 'eager' : 'lazy'}
            decoding="async"
            onError={handleImageError}
          />
          <div className="news-card-media-shade"></div>
          <div className="news-card-badges">
            <span className={isTruckersMP ? 'is-truckersmp' : 'is-mileage-riot'}>
              {isTruckersMP ? <FaTruck aria-hidden="true" /> : <FaStar aria-hidden="true" />}
              {isTruckersMP ? 'TruckersMP' : 'Mileage Riot'}
            </span>
            {featured && <span className="is-latest">{t('news.latest')}</span>}
          </div>
        </div>

        <div className="news-card-content">
          <div className="news-card-date">
            <FaCalendarAlt aria-hidden="true" />
            <time dateTime={item.created_at}>{date}</time>
          </div>
          <h2>{item.title}</h2>
          {description && <p>{description}</p>}
          <div className="news-card-footer">
            <span>{t('news.readMore')}</span>
            <FaArrowRight aria-hidden="true" />
          </div>
        </div>
      </Link>
    </article>
  );
};

const News = () => {
  const [allNews, setAllNews] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const { t, i18n } = useTranslation();
  const topRef = useRef();

  const locale = i18n.resolvedLanguage?.startsWith('en') ? 'en-GB' : 'ru-RU';

  useEffect(() => {
    const fetchNews = async () => {
      setLoading(true);
      setError(false);

      try {
        const response = await fetch('https://hub.mileageriot.com/api/news');
        if (!response.ok) throw new Error(`News request failed: ${response.status}`);
        const data = await response.json();
        if (!Array.isArray(data.data)) throw new Error('News response is not an array');
        setAllNews(data.data);
      } catch (fetchError) {
        console.error('Error fetching news:', fetchError);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, sourceFilter]);

  const filteredNews = useMemo(() => {
    const query = searchQuery.trim().toLocaleLowerCase(locale);

    return allNews.filter((item) => {
      const isTruckersMP = item.source === 'truckersmp';
      const matchesSource = sourceFilter === 'all'
        || (sourceFilter === 'truckersmp' && isTruckersMP)
        || (sourceFilter === 'mileageriot' && !isTruckersMP);
      if (!matchesSource) return false;
      if (!query) return true;

      return `${item.title || ''} ${cleanText(item.description)}`
        .toLocaleLowerCase(locale)
        .includes(query);
    });
  }, [allNews, locale, searchQuery, sourceFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredNews.length / PAGE_SIZE));
  const newsData = filteredNews.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const mileageRiotCount = allNews.filter((item) => item.source !== 'truckersmp').length;
  const truckersMpCount = allNews.length - mileageRiotCount;

  const goToPage = (nextPage) => {
    const safePage = Math.min(Math.max(nextPage, 1), totalPages);
    setPage(safePage);
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const paginationItems = Array.from({ length: totalPages }, (_, index) => index + 1)
    .filter((number) => number === 1 || number === totalPages || Math.abs(number - page) <= 2)
    .reduce((items, number, index, numbers) => {
      if (index > 0 && numbers[index - 1] !== number - 1) items.push(`dots-${number}`);
      items.push(number);
      return items;
    }, []);

  return (
    <>
      <Helmet>
        <title>Новости — Mileage Riot ВТК | ETS2 & ATS</title>
        <meta name="description" content="Новости виртуальной транспортной компании Mileage Riot — конвои, обновления, партнёрства, итоги месяца. ВТК ETS2 и ATS на TruckersMP." />
        <link rel="canonical" href="https://mileageriot.com/news" />
        <meta property="og:title" content="Новости — Mileage Riot ВТК" />
        <meta property="og:description" content="Новости виртуальной транспортной компании Mileage Riot." />
        <meta property="og:url" content="https://mileageriot.com/news" />
        <meta property="og:locale" content={locale === 'en-GB' ? 'en_GB' : 'ru_RU'} />
      </Helmet>

      <main className="news-page-upgraded page-news" ref={topRef}>
        <div className="news-page-grid-bg" aria-hidden="true"></div>
        <div className="news-page-glow news-page-glow-left" aria-hidden="true"></div>
        <div className="news-page-glow news-page-glow-right" aria-hidden="true"></div>

        <div className="container news-page-container">
          <header className="news-page-hero">
            <div className="news-page-hero-copy">
              <div className="section-badge news-page-badge">
                <FaNewspaper aria-hidden="true" />
                <span>{t('news.badge')}</span>
              </div>
              <h1>{t('news.title')}</h1>
              <p>{t('news.subtitle')}</p>
            </div>

            <div className="news-page-stats" aria-label={t('news.archiveSummary')}>
              <div><span>{t('news.allArticles')}</span><strong>{allNews.length || '—'}</strong></div>
              <div><span>Mileage Riot</span><strong>{loading ? '—' : mileageRiotCount}</strong></div>
              <div><span>TruckersMP</span><strong>{loading ? '—' : truckersMpCount}</strong></div>
            </div>
          </header>

          <div className="news-toolbar">
            <label className="news-search">
              <FaSearch aria-hidden="true" />
              <span className="visually-hidden">{t('news.searchLabel')}</span>
              <input
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder={t('news.searchPlaceholder')}
              />
            </label>

            <div className="news-source-filters" role="group" aria-label={t('news.sourceFilter')}>
              {[
                { value: 'all', label: t('news.allSources') },
                { value: 'mileageriot', label: 'Mileage Riot' },
                { value: 'truckersmp', label: 'TruckersMP' },
              ].map((filter) => (
                <button
                  type="button"
                  key={filter.value}
                  className={sourceFilter === filter.value ? 'active' : ''}
                  onClick={() => setSourceFilter(filter.value)}
                  aria-pressed={sourceFilter === filter.value}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            <span className="news-results-count">
              {t('news.found', { count: filteredNews.length })}
            </span>
          </div>

          {loading ? (
            <div className="news-page-state" role="status">
              <span className="news-page-loader" aria-hidden="true"></span>
              <p>{t('news.loadingList')}</p>
            </div>
          ) : error ? (
            <div className="news-page-state" role="alert">
              <FaNewspaper aria-hidden="true" />
              <h2>{t('news.error')}</h2>
              <p>{t('news.errorHint')}</p>
            </div>
          ) : newsData.length === 0 ? (
            <div className="news-page-state">
              <FaSearch aria-hidden="true" />
              <h2>{t('news.noResults')}</h2>
              <p>{t('news.noResultsHint')}</p>
            </div>
          ) : (
            <>
              <div className="news-editorial-grid" aria-live="polite">
                {newsData.map((item, index) => (
                  <NewsCard
                    key={item.id}
                    item={item}
                    featured={page === 1 && index === 0}
                    locale={locale}
                    t={t}
                  />
                ))}
              </div>

              {totalPages > 1 && (
                <nav className="news-pagination" aria-label={t('news.pagination')}>
                  <button type="button" onClick={() => goToPage(page - 1)} disabled={page === 1}>
                    <FaChevronLeft aria-hidden="true" />
                    <span>{t('news.previous')}</span>
                  </button>

                  <div className="news-pagination-pages">
                    {paginationItems.map((item) => typeof item === 'string' ? (
                      <span className="news-pagination-dots" key={item}>…</span>
                    ) : (
                      <button
                        type="button"
                        key={item}
                        className={item === page ? 'active' : ''}
                        onClick={() => goToPage(item)}
                        aria-current={item === page ? 'page' : undefined}
                        aria-label={t('news.pageLabel', { page: item })}
                      >
                        {item}
                      </button>
                    ))}
                  </div>

                  <button type="button" onClick={() => goToPage(page + 1)} disabled={page === totalPages}>
                    <span>{t('news.next')}</span>
                    <FaChevronRight aria-hidden="true" />
                  </button>
                </nav>
              )}
            </>
          )}
        </div>
      </main>
    </>
  );
};

export default News;
